import HighlightOffIcon from '@material-ui/icons/HighlightOff'
import LocationOnIcon from '@material-ui/icons/LocationOn'
import { forwardRef, useEffect, useRef, useState } from 'react'
import { Handler, HandlerOf } from 'shared/helpers/typeHelper'
import { usePlacesAutoCompleteOptions } from 'shared/hooks/usePlacesAutoCompleteOptions'
import styled from 'styled-components'

import { blueGray, informationL1, lightGray2, royalBlue } from '../../theme/colors'
import { TextInputProps } from '../TextInput/TextInput'
import { DropdownOption } from './Dropdown'
import { Option } from './Dropdown.styles'
import { SearchDropdown, SearchDropdownProps } from './SearchDropdown'

export interface AddressDropdownProps
  extends Omit<SearchDropdownProps, 'options' | 'width' | 'maxHeight' | 'onChange' | 'value'> {
  width?: number
  maxHeight?: number
  value?: string | null
  onChange?: HandlerOf<string>
  onTimeZoneOffsetChange?: HandlerOf<number | undefined>
  inputProps?: TextInputProps
  enableRawValues?: boolean
  searchTypes?: string
  includeZip?: boolean
  renderOption?: (label: string) => React.ReactNode
}

export const AddressDropdownStyledOptionWrap = styled(Option)`
  padding-top: 8px;
  padding-bottom: 6px;
  display: grid;
  grid-template-columns: 15px 1fr;
  align-items: flex-start;
  gap: 10px;
  width: calc(100% - 2px);

  &:hover {
    background-color: ${informationL1};
  }
`

export const AddressDropdownStyledLocationIcon = styled(LocationOnIcon)`
  color: ${blueGray};
  font-size: 24px;
  margin-top: -1px;
  margin-left: 2px;
`

export const AddressDropdownBlueStyledLocationIcon = styled(AddressDropdownStyledLocationIcon)`
  color: ${royalBlue};
  font-size: 20px;
  margin-top: 2px;
  margin-left: 0;
`

const StyledHighlightOffIcon = styled(HighlightOffIcon)`
  color: ${lightGray2};
  font-size: 24px;
`

const formatAddressOptions = (options: any) => {
  return options.map((option: any) => {
    return {
      label: option.description,
      value: option.description,
      utc_offset_minutes: option.utc_offset_minutes,
    }
  })
}

export const AddressDropdown = forwardRef(function AddressDropdown(props: AddressDropdownProps, ref: any) {
  const {
    onChange,
    onTimeZoneOffsetChange,
    inputProps,
    enableRawValues,
    renderOption,
    searchTypes,
    includeZip,
    ...restProps
  } = props
  const [width, setWidth] = useState(424)
  const [value, setValue] = useState(props.value || '')
  const wrapRef = useRef<HTMLDivElement | null>(null)
  const googlePlacesElement = useRef<HTMLDivElement>(null)
  const { options } = usePlacesAutoCompleteOptions(
    googlePlacesElement.current,
    value,
    searchTypes,
    formatAddressOptions,
    includeZip
  )

  useEffect(() => {
    if (wrapRef.current?.clientWidth) setWidth(wrapRef.current?.clientWidth)
  }, [])

  useEffect(() => {
    if (enableRawValues) onChange?.(value)
  }, [onChange, value, enableRawValues])

  useEffect(() => {
    setValue(props.value || '')
  }, [props.value])

  function handleLocationClick(location: string, closeDropdown: Handler, option: any) {
    onChange?.(location)
    onTimeZoneOffsetChange?.(option.utc_offset_minutes)
    closeDropdown()
  }

  function handleReset() {
    onChange?.('')
    setValue('')
  }

  const locationsOptions: DropdownOption<string>[] = options.map((location) => {
    const [street, ...rest] = location.label.split(',')

    return {
      label: location.label,
      value: location.value,
      renderer: (_, closeDropdown) => (
        <AddressDropdownStyledOptionWrap
          onClick={() => handleLocationClick(location.value, closeDropdown, location)}
          key={location.value}
        >
          {renderOption ? (
            renderOption(location.label)
          ) : (
            <>
              <AddressDropdownBlueStyledLocationIcon />
              {street} <br />
              {rest.join(',')}
            </>
          )}
        </AddressDropdownStyledOptionWrap>
      ),
    }
  })

  return (
    <div ref={wrapRef}>
      <div ref={googlePlacesElement}></div>
      <SearchDropdown
        onChange={setValue}
        options={locationsOptions}
        width={width}
        maxHeight={500}
        disableOptionFilter
        disableVirtualDropdown
        ref={ref}
        {...restProps}
        value={value}
        inputProps={{
          autocomplete: 'off',
          noMargin: true,
          startAdornment: <AddressDropdownStyledLocationIcon />,
          endAdornment: value ? <StyledHighlightOffIcon onClick={handleReset} /> : null,
          ...inputProps,
        }}
      />
    </div>
  )
})
